import * as inputOptions from 'common/constants/inputs';
import { MULTIPLE_PREMIUM_DISCOUNT_ALIAS } from 'common/constants/valuations';
import { RowFormat } from 'common/types/scalarSpreadsheet';
import { VALUATION_APPROACH_GPC_ALIAS } from 'pages/Valuations/approaches/backsolveApproach/constants';
import {
  COMPANY,
  MAIN_COLUMNS_IDS,
  PUBLIC_COMPS_APPROACH_ALIAS,
  SECOND_FORWARD_ID,
  SECOND_NON_FORWARD_ID,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/constants';
import handleOptionsNumberFormat from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/handleOptionsNumberFormat';
import {
  LTM_EBITDA_MULTIPLE_ID,
  NTM_EBITDA_MULTIPLE_ID,
} from 'pages/ValuationsAllocation/approaches/TransactionComps/config/constants';
import { formatNumbers, toString } from 'utilities';
import { aliasesForGenericOptions, getOptionsFrom, handlers } from './handlers';
import { CustomCell, GetOptionSelectedProps, Option, OptionListType } from './utilities.props';

// cell.value can be an integer or an object
export const getCellValue = (cell: CustomCell) => {
  if (cell.hidden) {
    return '';
  }
  const value = cell?.value;
  if (typeof value === 'object' && value !== null && 'id' in value) {
    return value.id;
  }
  return value;
};

export const getOptionSelected = ({ dataProps, useAliasList = true }: GetOptionSelectedProps) => {
  const {
    props,
    fundList,
    captableInfo,
    capTableList,
    useLabel,
    caseInsensitiveMatch,
    group,
    currency,
    defaultOptionList,
  } = dataProps;
  const { cell } = props;
  const { alias, columnId, readOnly, format, options: optionsFromCell } = cell;
  const handler = handlers[alias as keyof typeof handlers];

  let optionList: OptionListType = [];
  let defaultLabel = inputOptions.SELECT_OPTION;

  if (handler && useAliasList) {
    const { optionList: newOptionList, defaultLabel: newDefaultLabel } = handler({
      props,
      fundList,
      captableInfo,
      capTableList,
      group,
      optionsFromCell,
    });
    optionList = newOptionList ?? [];
    defaultLabel = newDefaultLabel ?? inputOptions.SELECT_OPTION;
  }

  if (aliasesForGenericOptions.includes(alias)) {
    optionList = getOptionsFrom(inputOptions.YES_NO);
  }

  if (defaultOptionList?.length !== 0 && optionList.length === 0) {
    optionList = getOptionsFrom(defaultOptionList as OptionListType);
  }

  if (alias === PUBLIC_COMPS_APPROACH_ALIAS) {
    optionList = optionList.map(opt => ({ id: opt?.panelId, label: opt?.name }));
  }

  if (alias === VALUATION_APPROACH_GPC_ALIAS) {
    optionList = getOptionsFrom(props.options as OptionListType);
  }

  // Only apply this logic on EBITDA columns for the company row
  if (
    !readOnly &&
    alias === COMPANY &&
    [SECOND_NON_FORWARD_ID, SECOND_FORWARD_ID, LTM_EBITDA_MULTIPLE_ID, NTM_EBITDA_MULTIPLE_ID].includes(
      columnId as string
    )
  ) {
    optionList = handleOptionsNumberFormat({
      options: defaultOptionList as Option[],
      format: format as RowFormat,
      currency,
    });
  }

  // Only apply this logic for multiple premium discount row
  if (!readOnly && alias === MULTIPLE_PREMIUM_DISCOUNT_ALIAS && MAIN_COLUMNS_IDS.includes(columnId as string)) {
    return formatNumbers({
      value: cell.value,
      currency: format?.style === 'currency' ? currency : null,
      format,
      prefix: null,
      suffix: null,
      allowNegativeValue: null,
      currencyCode: null,
      blankIfNull: null,
      staticCurrency: null,
    });
  }

  const itemSelected = optionList.find(opt => {
    // opt.value is used for all cases with the exception of Underlying Security which uses opt.id
    let optValue = opt?.value ?? opt?.id;
    if (useLabel) {
      optValue = opt?.label;
    }
    const cellValue = getCellValue(cell);
    if (caseInsensitiveMatch) {
      return toString(optValue).toLowerCase() === toString(cellValue).toLowerCase();
    }
    return toString(optValue) === toString(cellValue);
  });

  return itemSelected?.label || itemSelected?.name || (!cell.readOnly && defaultLabel);
};
